import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/utils'

export const CancelConfirmDialog: React.FC<{
  open: boolean
  onCancel: () => void
  onContinue: () => void
  variant?: 'cancel' | 'exit'
  className?: string
}> = ({ open, onCancel, onContinue, variant = 'exit', className = '' }) => {
  const { t } = useTranslation()

  // Freeze the variant when dialog opens to prevent title flickering during close animation
  const [frozenVariant, setFrozenVariant] = useState<'cancel' | 'exit'>(variant)

  useEffect(() => {
    if (open) {
      setFrozenVariant(variant)
    }
  }, [open, variant])

  const titleKey =
    frozenVariant === 'cancel'
      ? 'upload.cancelDialog.cancelTitle'
      : 'upload.cancelDialog.exitTitle'

  const titleDescriptionKey =
    frozenVariant === 'cancel'
      ? 'upload.cancelDialog.cancelDescription'
      : 'upload.cancelDialog.exitDescription'

  return (
    <Dialog open={open} onOpenChange={(newOpen) => !newOpen && onContinue()}>
      <DialogContent
        className={cn(
          'sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6',
          className
        )}
      >
        <DialogHeader className="space-y-2">
          <DialogTitle className="m-0 text-lg font-semibold text-gray-900">
            {t(titleKey)}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {t(titleDescriptionKey)}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex flex-row justify-stretch !space-x-0 gap-3 pt-4">
          <Button
            type="button"
            className="flex-1"
            variant="outlined"
            color="default"
            onClick={onContinue}
          >
            {t('upload.cancelDialog.continueUpload')}
          </Button>
          <Button
            type="button"
            className="flex-1 text-white bg-red-600 hover:bg-red-700 active:bg-red-800"
            variant="solid"
            onClick={onCancel}
          >
            {t('upload.cancelDialog.cancelUpload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
